import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin, Tag, Activity, CalendarClock, ExternalLink, Download, Workflow, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { VENDOR_STAGES_ALL, INVESTOR_PIPELINE, stageTone, typeBadge, ukDate, ukDateTime, gbp } from "./crmPipelineShared";
import { useStageMover } from "./useStageMover";
import { ReferralPanel } from "./ReferralPanel";
import { StageReviewBanner } from "./StageReviewBanner";
import { SourceBadge } from "./SourceBadge";
import { FollowUpControl } from "./FollowUpControl";
import { NotesPanel } from "./NotesPanel";
import { RecordLifecycle } from "./RecordLifecycle";
import { LoadError, errMsg, asText } from "./LoadError";
import { QuickActions } from "./QuickActions";
import { OutcomeSheet } from "./OutcomeSheet";
import { EmailPanel } from "./EmailPanel";

const touchLine = (t) => t ? [t.source || "Unknown", t.utm_campaign, t.landing_page, t.at ? ukDate(t.at) : ""].filter(Boolean).join(" · ") : "Unknown";
const scrollTo = (id) => document.querySelector(`[data-testid="${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });

const storeLabel = { investor: "Investor Lead", lead: "Vendor Lead", contact: "Contact" };

function Section({ title, icon: Icon, children, testId }) {
  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-brass" /><h3 className="text-sm font-semibold text-white">{title}</h3></div>
      {children}
    </div>
  );
}

export default function CrmContactRecord() {
  const { store, id } = useParams();
  const navigate = useNavigate();
  const [rec, setRec] = useState(null);
  const [hasReferral, setHasReferral] = useState(false);
  const [refKey, setRefKey] = useState(0);
  const onRefsLoaded = useCallback((refs) => setHasReferral(refs.some((r) => !r.voided && r.vendor_store === store && r.vendor_id === id)), [store, id]);
  const [loadErr, setLoadErr] = useState("");
  const [sheet, setSheet] = useState(null);

  const load = useCallback(() => {
    setLoadErr("");
    api.get(`/crm/unified-contacts/${store}/${id}`).then((r) => setRec(r.data))
      .catch((e) => setLoadErr(e?.response?.status === 404 ? "This record could not be found." : errMsg(e, "Could not load contact")));
  }, [store, id]);
  useEffect(load, [load]);

  const view = rec && rec.contact_type === "Investor" ? "investor" : "vendor";
  const stages = view === "investor" ? INVESTOR_PIPELINE : VENDOR_STAGES_ALL;
  const mover = useStageMover({ view, onMoved: () => { load(); setRefKey((k) => k + 1); } });
  const onStageSelect = (stage) => mover.request({ store, id, hasReferral, conversion: rec?.conversion, appointment_at: rec?.appointment_at }, stage);

  const setFollowup = async (date) => {
    try {
      await api.post(`/crm/unified-contacts/${store}/${id}/followup`, { date });
      toast.success(date ? "Follow-up updated" : "Follow-up cleared");
      load();
    } catch (e) { toast.error(errMsg(e, "Could not set follow-up")); }
  };

  if (loadErr && !rec) return <CrmShell title="Contact"><LoadError message={loadErr} onRetry={load} testId="record-load-error" /></CrmShell>;
  if (!rec) return <CrmShell title="Contact"><div className="text-emerald-100/50 text-sm py-10 text-center">Loading…</div></CrmShell>;

  const name = [rec.first_name, rec.last_name].filter(Boolean).join(" ") || "Unnamed contact";
  const crit = rec.criteria || {};
  const inactive = rec.record_state && rec.record_state !== "active";
  const critRows = [
    ["Locations", asText(crit.locations)],
    ["Strategies", asText(crit.strategies)],
    ["Budget", crit.min_price || crit.max_price ? `£${crit.min_price || 0} – £${crit.max_price || "?"}` : ""],
    ["Available cash", crit.available_cash ? `£${crit.available_cash}` : ""],
    ["Finance", crit.finance_type],
    ["Buying capacity", crit.buying_capacity],
  ].filter(([, v]) => v);

  return (
    <CrmShell title="Contact">
      {mover.dialog}
      <OutcomeSheet open={!!sheet} onOpenChange={(o) => !o && setSheet(null)} mode={sheet || "outcome"} target={{ store, id, name }} onSaved={load} />
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-emerald-100/60 hover:text-white mb-4" data-testid="record-back"><ArrowLeft className="w-4 h-4" /> Back</button>
      <StageReviewBanner store={store} id={id} review={rec.stage_review} onCleared={load} />
      {inactive && <div className="mb-4"><RecordLifecycle store={store} id={id} state={rec.record_state} onDone={load} /></div>}

      {/* Header */}
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5 mb-5" data-testid="record-header">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="text-xl font-serif text-white">{name}</h2>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${typeBadge(rec.contact_type)}`} data-testid="record-type">{rec.contact_type}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${stageTone(rec.pipeline_stage || stages[0])}`} data-testid="record-stage-badge">{rec.pipeline_stage || stages[0]}</span>
            </div>
            <div className="flex flex-wrap gap-4 mt-2.5 text-sm">
              {rec.mobile && <a href={`tel:${rec.mobile}`} className="flex items-center gap-1.5 text-emerald-100/70 hover:text-brass"><Phone className="w-3.5 h-3.5" />{rec.mobile}</a>}
              {rec.email && <a href={`mailto:${rec.email}`} className="flex items-center gap-1.5 text-emerald-100/70 hover:text-brass"><Mail className="w-3.5 h-3.5" />{rec.email}</a>}
              {rec.address && <span className="flex items-center gap-1.5 text-emerald-100/60"><MapPin className="w-3.5 h-3.5" />{rec.address}</span>}
            </div>
            <div className="mt-2"><SourceBadge source={rec.source} testId="record-source-badge" /></div>
            <div className="text-xs text-emerald-100/40 mt-2">Source: {rec.source || "—"} · Added {ukDate(rec.created_at)}{rec.marketing_status ? ` · Marketing: ${rec.marketing_status}` : ""}</div>
          </div>
        </div>

        {!inactive && <div className="mt-4"><QuickActions mobile={rec.mobile} email={rec.email} onEmail={() => { scrollTo("email-panel"); setTimeout(() => document.querySelector('[data-testid="email-subject"]')?.focus(), 400); }}
          onOutcome={() => setSheet("outcome")} onAppointment={() => setSheet("appointment")} onFollowup={() => setSheet("followup")}
          onNote={() => { scrollTo("note-input"); setTimeout(() => document.querySelector('[data-testid="note-input"]')?.focus(), 400); }}
          onStatus={() => scrollTo("record-stage-select")} /></div>}

        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 mt-4 text-xs" data-testid="record-summary">
          <div><dt className="text-emerald-100/50">Last contact</dt><dd className="text-white mt-0.5" data-testid="record-last-contact">{rec.last_contact ? ukDateTime(rec.last_contact) : rec.last_attempt ? `Attempted ${ukDateTime(rec.last_attempt)}` : "Not yet"}</dd></div>
          <div><dt className="text-emerald-100/50">Next action</dt><dd className="text-white mt-0.5" data-testid="record-next-action">{rec.next_follow_up ? ukDate(rec.next_follow_up) : <span className="text-amber-200">None set</span>}</dd></div>
          <div><dt className="text-emerald-100/50">Appointment</dt><dd className="text-white mt-0.5" data-testid="record-appointment">{rec.appointment_at ? ukDateTime(rec.appointment_at) : "—"}</dd></div>
          <div><dt className="text-emerald-100/50">Heard about us</dt><dd className="text-white mt-0.5" data-testid="record-heard">{rec.self_reported_source || "—"}</dd></div>
          <div className="col-span-2"><dt className="text-emerald-100/50">Original source</dt><dd className="text-white mt-0.5 break-words" data-testid="record-first-touch">{rec.first_touch ? touchLine(rec.first_touch) : (rec.source || "Unknown")}</dd></div>
          <div className="col-span-2"><dt className="text-emerald-100/50">Latest source</dt><dd className="text-white mt-0.5 break-words" data-testid="record-latest-touch">{rec.latest_touch ? touchLine(rec.latest_touch) : "Same as original"}</dd></div>
        </dl>

        {/* Pipeline control */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-emerald-100/50 flex items-center gap-1"><Workflow className="w-3.5 h-3.5" /> Pipeline stage:</span>
          <select value={rec.pipeline_stage && stages.includes(rec.pipeline_stage) ? rec.pipeline_stage : stages[0]} onChange={(e) => onStageSelect(e.target.value)}
            disabled={inactive} data-testid="record-stage-select" className="disabled:opacity-50 bg-[#091E16] border border-white/15 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-brass">
            {stages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <Link to={`/crm/pipeline?view=${view}`} className="text-xs text-brass hover:underline ml-1">View board</Link>
        </div>
        {["Lost", "Not Interested", "Withdrawn"].includes(rec.pipeline_stage) && rec.lost_reason && <div className="mt-2 text-xs text-red-300/80">Lost reason: {rec.lost_reason}</div>}
        {rec.conversion && (
          <div className="mt-4 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-4 py-3 flex items-center gap-5 flex-wrap" data-testid="record-conversion">
            <div className="flex items-center gap-1.5 text-emerald-300 text-sm font-semibold"><TrendingUp className="w-4 h-4" /> Completed deal</div>
            <div className="text-xs text-emerald-100/80">Type: <span className="text-white">{rec.conversion.transaction_type || "—"}</span></div>
            <div className="text-xs text-emerald-100/80">Value: <span className="text-white font-mono">{gbp(rec.conversion.price)}</span></div>
            <div className="text-xs text-emerald-100/80">EC fee: <span className="text-emerald-300 font-mono">{gbp(rec.conversion.fee)}</span></div>
            <div className="text-xs text-emerald-100/80">Date: <span className="text-white">{ukDate(rec.conversion.completion_date)}</span></div>
          </div>
        )}

        {/* Follow-up quick actions */}
        <div className="mt-4">
          {!inactive && <FollowUpControl value={rec.next_follow_up} onSet={setFollowup} testIdPrefix="record-followup" />}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          {critRows.length > 0 && (
            <Section title="Investment criteria" icon={Tag} testId="record-criteria">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {critRows.map(([k, v]) => (<div key={k}><dt className="text-[11px] text-emerald-100/50">{k}</dt><dd className="text-emerald-100/90">{v}</dd></div>))}
              </dl>
            </Section>
          )}

          {(rec.tags?.length > 0 || rec.resource_downloads?.length > 0) && (
            <Section title="Tags & downloads" icon={Download} testId="record-tags">
              {rec.tags?.length > 0 && <div className="flex flex-wrap gap-1.5 mb-3">{rec.tags.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-white/10 text-emerald-100/70">{t}</span>)}</div>}
              {rec.resource_downloads?.length > 0 && <ul className="text-xs text-emerald-100/70 space-y-1">{rec.resource_downloads.map((r, i) => <li key={i} className="flex items-center gap-1.5"><Download className="w-3 h-3 text-brass" />{typeof r === "string" ? r : (r.title || r.slug || "Resource")}</li>)}</ul>}
            </Section>
          )}

          {rec.properties?.length > 0 && (
            <Section title="Linked properties" icon={MapPin} testId="record-properties">
              <ul className="text-sm text-emerald-100/80 space-y-1.5">{rec.properties.map((p, i) => <li key={i}>{p.address || "—"}{p.deal_id ? ` · ${p.deal_id}` : ""}</li>)}</ul>
            </Section>
          )}

          {rec.appointments?.length > 0 && (
            <Section title="Appointment history" icon={CalendarClock} testId="record-appointments">
              <ul className="text-sm text-emerald-100/80 space-y-1.5">{rec.appointments.map((a, i) => <li key={i}>{/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}$/.test(a.note || "") ? ukDateTime(a.note.replace(" ", "T")) : (a.note || a.type)} <span className="text-[11px] text-emerald-100/40">· logged {ukDateTime(a.created_at)}</span></li>)}</ul>
            </Section>
          )}
          <EmailPanel store={store} id={id} hasEmail={!!rec.email} onSent={load} />
          {view === "vendor" && <ReferralPanel store={store} id={id} refreshKey={refKey} onLoaded={onRefsLoaded} />}
          <NotesPanel recordType={store} recordId={id} onAdded={load} readOnly={inactive} />
          {!inactive && <Section title="Record management" icon={ExternalLink} testId="record-manage"><RecordLifecycle store={store} id={id} onDone={(m) => (m === "restore" ? load() : navigate("/crm/contacts"))} /></Section>}

          <Section title="Linked records" icon={ExternalLink} testId="record-linked">
            <ul className="space-y-2">
              {(rec.linked_records || []).map((l) => (
                <li key={`${l.store}-${l.id}`}>
                  <Link to={l.link} className="flex items-center justify-between text-sm text-emerald-100/80 hover:text-brass bg-white/5 rounded-lg px-3 py-2">
                    <span>{storeLabel[l.store] || l.store}{l.stage ? ` · ${l.stage}` : ""}</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        </div>

        <Section title="Activity timeline" icon={Activity} testId="record-timeline">
          {(rec.timeline || []).length === 0 && <p className="text-emerald-100/50 text-sm py-6 text-center">No activity yet.</p>}
          <ol className="space-y-3">
            {(rec.timeline || []).map((a, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <div className="w-2 h-2 rounded-full bg-brass mt-1.5 shrink-0" />
                <div>
                  <div className="text-white">{a.type}{a.note ? <span className="text-emerald-100/60"> — {a.note}</span> : ""}</div>
                  <div className="text-[11px] text-emerald-100/40">{ukDateTime(a.created_at)}{a.actor ? ` · ${a.actor}` : ""}{a.source ? ` · ${a.source}` : ""}</div>
                </div>
              </li>
            ))}
          </ol>
        </Section>
      </div>
    </CrmShell>
  );
}
